'use client';

import { c as _c } from "react/compiler-runtime";
import type { MarketId } from '@morpho-org/blue-sdk';
import { InterfaceChainUtils, type InterfaceToken, type MarketForRewards } from '@morpho-org/dapp-core';
import { Avatar, NetworkAvatar as Network } from '@morpho-org/ui-v6/client';
import { Card } from '@morpho-org/ui-v6/server';
import { cn } from '@morpho-org/ui-v6/utils';
import { memo, useMemo } from 'react';
import { IconRewardHover } from '@/components/common/IconRewardHover';
import ValueTransition from '@/components/common/ValueTransition';
import { useMarketLendSummaryData } from './hooks/useMarketLendSummaryData';
interface MarketLendSummaryCardProps {
  marketId: MarketId;
  /** Current on-chain loan-asset supply. */
  currentSupply: bigint | undefined;
  /** Supply after the pending lend (undefined ⇒ no "after" value shown). */
  afterSupply: bigint | undefined;
  loanAsset: InterfaceToken;
  isLoading: boolean;
  avgSupplyApy?: number | null;
  marketForRewards?: MarketForRewards | null;
  /** Card surface — same convention as `MarketSummaryCard`. */
  background?: 'card' | 'muted';
}

/**
 * Lend-mode summary — the supply-side sister of `MarketSummaryCard`. The lender's
 * position is one figure (the supplied loan assets), so the card shows Network,
 * the Supply before → after transition, and the net supply APY (native + supply
 * rewards); the borrow-side rows (Collateral / Loan / LTV / Liq. LTV) don't apply.
 */
const MarketLendSummaryCard = t0 => {
  const $ = _c(104);
  const {
    marketId,
    currentSupply,
    afterSupply,
    loanAsset,
    isLoading,
    avgSupplyApy,
    marketForRewards,
    background: t1
  } = t0;
  const background = t1 === undefined ? "card" : t1;
  const chainId = loanAsset.chain.id;
  let t2;
  if ($[0] !== afterSupply || $[1] !== avgSupplyApy || $[2] !== currentSupply || $[3] !== loanAsset || $[4] !== marketForRewards) {
    t2 = {
      initialSupply: currentSupply,
      finalSupply: afterSupply,
      loanAsset,
      avgSupplyApy,
      marketForRewards
    };
    $[0] = afterSupply;
    $[1] = avgSupplyApy;
    $[2] = currentSupply;
    $[3] = loanAsset;
    $[4] = marketForRewards;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    supply,
    apy: apyData,
    projectedEarnings
  } = useMarketLendSummaryData(t2);
  let t3;
  if ($[6] !== chainId) {
    t3 = InterfaceChainUtils.getMetadata(chainId);
    $[6] = chainId;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const networkName = t3.chain.name;
  const t4 = background === "muted" && "bg-muted shadow-none";
  let t5;
  if ($[8] !== t4) {
    t5 = cn("gap-sm flex-col justify-between", t4);
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = background === "card";
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="text-3xs text-secondary-foreground">Network</span>;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      size: "small"
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== chainId) {
    t9 = <Network chainId={chainId} srcAvatarProps={t8} />;
    $[12] = chainId;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== networkName) {
    t10 = <span className="text-3xs">{networkName}</span>;
    $[14] = networkName;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t9) {
    t11 = <div className="flex min-h-[20px] flex-row items-center justify-between">{t7}<div className="gap-2xs flex flex-row items-center">{t9}{t10}</div></div>;
    $[16] = t10;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== loanAsset.logoURI) {
    t12 = <Avatar size="small" src={loanAsset.logoURI} />;
    $[19] = loanAsset.logoURI;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      maxWidth: 240
    };
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const t14 = `Lent (${loanAsset.symbol})`;
  let t15;
  if ($[22] !== t14) {
    t15 = <span className="text-3xs text-secondary-foreground truncate" style={t13}>{t14}</span>;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t12 || $[25] !== t15) {
    t16 = <div className="gap-2xs flex max-w-[150px] flex-none items-center overflow-hidden">{t12}{t15}</div>;
    $[24] = t12;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const t17 = supply.showFinalValue ? "text-muted-foreground" : "text-foreground";
  let t18;
  if ($[27] !== supply.initial || $[28] !== supply.initialDetailed || $[29] !== t17) {
    t18 = {
      value: supply.initial,
      tooltip: supply.initialDetailed,
      colorClass: t17
    };
    $[27] = supply.initial;
    $[28] = supply.initialDetailed;
    $[29] = t17;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== supply.final || $[32] !== supply.finalDetailed) {
    t19 = {
      value: supply.final,
      tooltip: supply.finalDetailed,
      colorClass: "text-foreground"
    };
    $[31] = supply.final;
    $[32] = supply.finalDetailed;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== isLoading) {
    t20 = {
      isLoading
    };
    $[34] = isLoading;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== supply.showFinalValue) {
    t21 = {
      showFinalValue: supply.showFinalValue,
      testId: "supply-value"
    };
    $[36] = supply.showFinalValue;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t18 || $[39] !== t19 || $[40] !== t20 || $[41] !== t21) {
    t22 = <div className="flex flex-[1px] justify-end"><ValueTransition initial={t18} final={t19} loading={t20} config={t21} /></div>;
    $[38] = t18;
    $[39] = t19;
    $[40] = t20;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== t16 || $[44] !== t22) {
    t23 = <div className="flex min-h-[20px] flex-row">{t16}{t22}</div>;
    $[43] = t16;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <span className="text-3xs text-secondary-foreground" style={{
      lineHeight: "normal"
    }}>APY</span>;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== apyData.apy || $[48] !== apyData.netApy || $[49] !== apyData.rewards || $[50] !== loanAsset) {
    t25 = {
      apy: apyData.apy,
      rewards: apyData.rewards,
      variant: "supply",
      netApy: apyData.netApy,
      asset: loanAsset
    };
    $[47] = apyData.apy;
    $[48] = apyData.netApy;
    $[49] = apyData.rewards;
    $[50] = loanAsset;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== chainId || $[53] !== loanAsset.address || $[54] !== marketId) {
    t26 = {
      kind: "market",
      chainId,
      marketId,
      loanTokenAddress: loanAsset.address
    };
    $[52] = chainId;
    $[53] = loanAsset.address;
    $[54] = marketId;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== apyData.formattedNetApy) {
    t27 = <span className="text-3xs" data-testid="market-lend-summary-rate">{apyData.formattedNetApy}</span>;
    $[56] = apyData.formattedNetApy;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== t25 || $[59] !== t26 || $[60] !== t27) {
    t28 = <div className="flex min-h-[20px] flex-row items-center justify-between">{t24}<IconRewardHover RewardsHoverCardProps={t25} merklContext={t26} isReversed={true} iconSize={20}>{t27}</IconRewardHover></div>;
    $[58] = t25;
    $[59] = t26;
    $[60] = t27;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <span className="text-3xs text-secondary-foreground" style={{
      lineHeight: "normal"
    }}>Projected monthly earnings</span>;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  const t30 = projectedEarnings.monthly.showFinalValue ? "text-muted-foreground" : "text-foreground";
  let t31;
  if ($[63] !== projectedEarnings.monthly.initial || $[64] !== projectedEarnings.monthly.initialTooltip || $[65] !== t30) {
    t31 = {
      value: projectedEarnings.monthly.initial,
      tooltip: projectedEarnings.monthly.initialTooltip,
      colorClass: t30
    };
    $[63] = projectedEarnings.monthly.initial;
    $[64] = projectedEarnings.monthly.initialTooltip;
    $[65] = t30;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] !== projectedEarnings.monthly.final || $[68] !== projectedEarnings.monthly.finalTooltip) {
    t32 = {
      value: projectedEarnings.monthly.final,
      tooltip: projectedEarnings.monthly.finalTooltip,
      colorClass: "text-foreground"
    };
    $[67] = projectedEarnings.monthly.final;
    $[68] = projectedEarnings.monthly.finalTooltip;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== isLoading) {
    t33 = {
      isLoading
    };
    $[70] = isLoading;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== projectedEarnings.monthly.showFinalValue) {
    t34 = {
      showFinalValue: projectedEarnings.monthly.showFinalValue,
      testId: "projected-monthly-earnings"
    };
    $[72] = projectedEarnings.monthly.showFinalValue;
    $[73] = t34;
  } else {
    t34 = $[73];
  }
  let t35;
  if ($[74] !== t31 || $[75] !== t32 || $[76] !== t33 || $[77] !== t34) {
    t35 = <div className="flex min-h-[20px] flex-row items-center justify-between">{t29}<ValueTransition initial={t31} final={t32} loading={t33} config={t34} /></div>;
    $[74] = t31;
    $[75] = t32;
    $[76] = t33;
    $[77] = t34;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <span className="text-3xs text-secondary-foreground" style={{
      lineHeight: "normal"
    }}>Projected yearly earnings</span>;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  const t37 = projectedEarnings.yearly.showFinalValue ? "text-muted-foreground" : "text-foreground";
  let t38;
  if ($[80] !== projectedEarnings.yearly.initial || $[81] !== projectedEarnings.yearly.initialTooltip || $[82] !== t37) {
    t38 = {
      value: projectedEarnings.yearly.initial,
      tooltip: projectedEarnings.yearly.initialTooltip,
      colorClass: t37
    };
    $[80] = projectedEarnings.yearly.initial;
    $[81] = projectedEarnings.yearly.initialTooltip;
    $[82] = t37;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  let t39;
  if ($[84] !== projectedEarnings.yearly.final || $[85] !== projectedEarnings.yearly.finalTooltip) {
    t39 = {
      value: projectedEarnings.yearly.final,
      tooltip: projectedEarnings.yearly.finalTooltip,
      colorClass: "text-foreground"
    };
    $[84] = projectedEarnings.yearly.final;
    $[85] = projectedEarnings.yearly.finalTooltip;
    $[86] = t39;
  } else {
    t39 = $[86];
  }
  let t40;
  if ($[87] !== isLoading) {
    t40 = {
      isLoading
    };
    $[87] = isLoading;
    $[88] = t40;
  } else {
    t40 = $[88];
  }
  let t41;
  if ($[89] !== projectedEarnings.yearly.showFinalValue) {
    t41 = {
      showFinalValue: projectedEarnings.yearly.showFinalValue,
      testId: "projected-yearly-earnings"
    };
    $[89] = projectedEarnings.yearly.showFinalValue;
    $[90] = t41;
  } else {
    t41 = $[90];
  }
  let t42;
  if ($[91] !== t38 || $[92] !== t39 || $[93] !== t40 || $[94] !== t41) {
    t42 = <div className="flex min-h-[20px] flex-row items-center justify-between">{t36}<ValueTransition initial={t38} final={t39} loading={t40} config={t41} /></div>;
    $[91] = t38;
    $[92] = t39;
    $[93] = t40;
    $[94] = t41;
    $[95] = t42;
  } else {
    t42 = $[95];
  }
  let t43;
  if ($[96] !== t11 || $[97] !== t23 || $[98] !== t28 || $[99] !== t35 || $[100] !== t42 || $[101] !== t5 || $[102] !== t6) {
    t43 = <Card className={t5} withBorder={t6}>{t11}{t23}{t28}{t35}{t42}</Card>;
    $[96] = t11;
    $[97] = t23;
    $[98] = t28;
    $[99] = t35;
    $[100] = t42;
    $[101] = t5;
    $[102] = t6;
    $[103] = t43;
  } else {
    t43 = $[103];
  }
  return t43;
};
export default memo(MarketLendSummaryCard);